import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { CalendarIcon, ClockIcon, MapPinIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { format, parseISO, differenceInSeconds } from 'date-fns';

interface TournamentData {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  venue: string | null;
  status: string;
}

interface EventItem {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  description: string | null;
}

interface DayGroup {
  date: string;
  events: EventItem[];
}

function useCountdown(targetDate: string) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calc = () => {
      const diff = differenceInSeconds(parseISO(targetDate), new Date());
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return {
        days: Math.floor(diff / 86400),
        hours: Math.floor((diff % 86400) / 3600),
        minutes: Math.floor((diff % 3600) / 60),
        seconds: diff % 60,
      };
    };
    setTimeLeft(calc());
    const interval = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return timeLeft;
}

function CountdownUnit({ value, label, delay }: { value: number; label: string; delay: number }) {
  return (
    <motion.div
      initial={{ scale: 0, rotateY: 90 }}
      animate={{ scale: 1, rotateY: 0 }}
      transition={{ type: 'spring', stiffness: 200, damping: 15, delay }}
      className="flex flex-col items-center"
    >
      <div className="relative w-14 h-14 flex items-center justify-center">
        <div className="absolute inset-0 bg-golf-green/20 backdrop-blur-md rounded-xl border border-white/10" />
        <AnimatePresence mode="popLayout">
          <motion.span
            key={value}
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="relative text-xl font-display font-bold text-white"
          >
            {String(value).padStart(2, '0')}
          </motion.span>
        </AnimatePresence>
      </div>
      <span className="mt-1 text-[9px] font-medium text-white/70 uppercase tracking-wider">{label}</span>
    </motion.div>
  );
}

function ScheduleCard({ event, index }: { event: EventItem; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-30px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.5, delay: index * 0.08, type: 'spring', stiffness: 120 }}
      className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 overflow-hidden"
    >
      <div className="flex items-start gap-3">
        <motion.div
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : {}}
          transition={{ delay: index * 0.08 + 0.15, type: 'spring' }}
          className="flex-shrink-0 w-9 h-9 bg-golf-orange/20 rounded-lg flex items-center justify-center"
        >
          <ClockIcon className="w-4.5 h-4.5 text-golf-orange" />
        </motion.div>
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-display font-semibold text-sm leading-tight">
            {event.title}
          </h4>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-white/60">
            <span className="flex items-center gap-1">
              <ClockIcon className="w-3.5 h-3.5" />
              {event.time}
            </span>
            {event.location && (
              <span className="flex items-center gap-1">
                <MapPinIcon className="w-3.5 h-3.5" />
                {event.location}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function DaySection({ group, dayIndex }: { group: DayGroup; dayIndex: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  let formattedDate = group.date;
  try {
    formattedDate = format(parseISO(group.date), 'EEE, MMM d');
  } catch {}

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.4, delay: dayIndex * 0.1 }}
        className="flex items-center gap-3 mb-3"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : {}}
          transition={{ delay: dayIndex * 0.1 + 0.15, type: 'spring', stiffness: 200 }}
          className="w-8 h-8 rounded-full bg-golf-orange flex items-center justify-center shadow-lg shadow-golf-orange/30"
        >
          <CalendarIcon className="w-4 h-4 text-white" />
        </motion.div>
        <h3 className="text-base font-display font-bold text-white">
          {formattedDate}
        </h3>
      </motion.div>

      <div className="ml-4 pl-7 border-l border-golf-orange/20 space-y-2.5">
        {group.events.map((event, idx) => (
          <ScheduleCard key={event.id} event={event} index={idx} />
        ))}
      </div>
    </motion.div>
  );
}

function FloatingOrb({ delay, size, x, y }: { delay: number; size: number; x: string; y: string }) {
  return (
    <motion.div
      className="absolute rounded-full bg-golf-orange/8 blur-2xl pointer-events-none"
      style={{ width: size, height: size, left: x, top: y }}
      animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
      transition={{ duration: 6, repeat: Infinity, delay, ease: 'easeInOut' }}
    />
  );
}

export default function MobilePromo() {
  const [tournament, setTournament] = useState<TournamentData | null>(null);
  const [days, setDays] = useState<DayGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [orgLogo, setOrgLogo] = useState<string | null>(null);

  const countdown = useCountdown(tournament?.start_date ?? new Date().toISOString());

  useEffect(() => {
    async function load() {
      const { data: tournaments } = await supabase
        .from('tournaments')
        .select('id, name, start_date, end_date, venue, status')
        .in('status', ['upcoming', 'ongoing', 'active'])
        .order('start_date', { ascending: true })
        .limit(1);

      const t = tournaments?.[0] ?? null;
      setTournament(t);

      if (t) {
        const { data: events } = await supabase
          .from('events')
          .select('id, title, date, time, location, description')
          .eq('tournament_id', t.id)
          .order('date')
          .order('time');

        if (events && events.length > 0) {
          const grouped: Record<string, EventItem[]> = {};
          for (const ev of events) {
            if (!grouped[ev.date]) grouped[ev.date] = [];
            grouped[ev.date].push(ev);
          }
          setDays(
            Object.entries(grouped)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([date, evts]) => ({ date, events: evts }))
          );
        }
      }

      const { data: logo } = await supabase
        .from('logos')
        .select('url')
        .eq('type', 'primary')
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (logo?.url) setOrgLogo(logo.url);
      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-golf-orange border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-golf-gray overflow-y-auto overflow-x-hidden">
      {/* Ambient orbs */}
      <FloatingOrb delay={0} size={160} x="5%" y="10%" />
      <FloatingOrb delay={2} size={140} x="65%" y="30%" />
      <FloatingOrb delay={3} size={120} x="20%" y="65%" />

      {/* Hero */}
      <section className="relative flex flex-col items-center justify-center px-4 pt-10 pb-8 min-h-[60dvh]">
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, type: 'spring', stiffness: 100 }}
          className="text-center w-full"
        >
          {orgLogo && (
            <motion.img
              src={orgLogo}
              alt="Logo"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="h-14 mx-auto mb-4 object-contain"
            />
          )}

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="text-2xl font-display font-bold text-white leading-tight px-2"
          >
            {tournament?.name || 'Tournament'}
          </motion.h1>

          {tournament?.venue && (
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="mt-3 text-sm text-white/60 flex items-center justify-center gap-1.5"
            >
              <MapPinIcon className="w-4 h-4 text-golf-orange" />
              {tournament.venue}
            </motion.p>
          )}

          {tournament?.start_date && (
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="mt-1.5 text-xs text-white/45"
            >
              {format(parseISO(tournament.start_date), 'MMM d')} &ndash; {format(parseISO(tournament.end_date), 'MMM d, yyyy')}
            </motion.p>
          )}
        </motion.div>

        {/* Countdown */}
        {tournament && differenceInSeconds(parseISO(tournament.start_date), new Date()) > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-8"
          >
            <p className="text-center text-white/45 text-[10px] uppercase tracking-widest mb-2">Starts in</p>
            <div className="flex gap-2.5">
              <CountdownUnit value={countdown.days} label="Days" delay={0.9} />
              <CountdownUnit value={countdown.hours} label="Hrs" delay={1.0} />
              <CountdownUnit value={countdown.minutes} label="Min" delay={1.1} />
              <CountdownUnit value={countdown.seconds} label="Sec" delay={1.2} />
            </div>
          </motion.div>
        )}

        {/* Scroll hint */}
        {days.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8 }}
            className="mt-6"
          >
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              className="flex flex-col items-center text-white/35"
            >
              <span className="text-[10px] uppercase tracking-wider mb-0.5">Schedule</span>
              <ChevronDownIcon className="w-4 h-4" />
            </motion.div>
          </motion.div>
        )}
      </section>

      {/* Schedule */}
      {days.length > 0 && (
        <section className="relative px-4 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl font-display font-bold text-white">Schedule</h2>
            <div className="mt-3 h-0.5 w-14 mx-auto bg-gradient-to-r from-golf-orange to-golf-orange/20 rounded-full" />
          </motion.div>

          <div className="space-y-8">
            {days.map((group, idx) => (
              <DaySection key={group.date} group={group} dayIndex={idx} />
            ))}
          </div>
        </section>
      )}

      {days.length === 0 && !loading && (
        <section className="px-4 pb-16 text-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="text-white/45 text-sm"
          >
            Schedule details coming soon.
          </motion.p>
        </section>
      )}
    </div>
  );
}
